import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import intlShape from '@/shared/utils/intlShape';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import EnumSelect from '@/shared/components/input/EnumSelect';
import DatePicker from '@/shared/components/input/DatePicker';
import YearSelect from '@/shared/components/input/YearSelect';
import enumCache from '@/shared/services/EnumCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { falseFunc, isBeforeMinimum } from '@/shared/utils/validationHelpers';
import messages from '../taskMessages';
import TaskDetailsParameterValue from '../taskDetails/TaskDetailsParameterValue';

// BatchTaskField.Parameter (see backend enum): marker value, not a selectable option by itself -
// it is expanded below into one dropdown entry per eligible dynamic task parameter.
const PARAMETER_FIELD = 4;

// Computed and relation-field parameters don't have a directly settable value (their value is derived),
// so they're not offered in the bulk "wijzig hoofdtaakveld" action.
const isEligibleParameterDefinition = definition => !definition.relationFieldType;

const defaultParameterValue = () => ({
  valueText: null,
  valueNumeric: null,
  valueDate: null,
  valueBoolean: false,
});

export default class ChangeMainTaskFieldModal extends Component {
  state = {
    field: 0,
    selectedTaskStatus: 2,
    taskstatuses: [],
    deadline: moment.utc().startOf('day'),
    executionDate: moment.utc().startOf('day'),
    referenceType: 1,
    referenceYear: moment
      .utc()
      .startOf('day')
      .year(),
    fieldOptions: [],
    selectedParameterDefinitionId: null,
    parameterValue: defaultParameterValue(),
  };

  componentDidMount() {
    this.form = React.createRef();
    const { intl } = this.props;
    enumCache.get('taskstatus').then(data => {
      this.setState({
        taskstatuses: [
          ...data.map(x => ({
            key: x.key,
            label: intl.formatMessage(listItemMsg('taskstatus', x.key, x.value)),
          })),
          {
            key: 4,
            label: intl.formatMessage(listItemMsg('extendedtaskstatus', 4, 4)),
          },
          {
            key: 6,
            label: intl.formatMessage(listItemMsg('extendedtaskstatus', 6, 6)),
          },
        ],
      });
    });
    enumCache.get('BatchTaskField').then(data => {
      const fieldOptions = data
        .filter(x => x.key !== PARAMETER_FIELD)
        .map(x => ({
          id: x.key,
          label: intl.formatMessage(listItemMsg('BatchTaskField', x.key, x.value)),
        }))
        .sort(dynamicSort('label'));
      this.setState({ fieldOptions });
    });
  }

  componentDidUpdate(prevProps) {
    const { show } = this.props;

    if (prevProps.show !== show) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(() => ({
        selectedTaskStatus: 2,
      }));
    }
  }

  static propTypes = {
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
    // Same dynamic task-parameter definitions already loaded for the "toon velden" column picker
    // (TaskOverviewContainer state.parameterDefinitions), reused here so both lists stay in sync.
    parameterDefinitions: PropTypes.arrayOf(PropTypes.shape({})),
  };

  static defaultProps = {
    show: false,
    parameterDefinitions: [],
  };

  taskStatusChanged = event => {
    this.setState({
      selectedTaskStatus: parseInt(event.target.value),
    });
  };
  validate() {
    const {
      selectedTaskStatus,
      field,
      deadline,
      executionDate,
      referenceType,
      referenceYear,
      selectedParameterDefinitionId,
    } = this.state;
    if (field === 0 && isBeforeMinimum(deadline)) return false;
    if (field === 1 && !selectedTaskStatus && selectedTaskStatus !== 0) return false;
    if (field === 2) {
      if (!referenceType) return false;
      if (referenceType === 0 && !moment.isMoment(deadline)) return false;
      if (referenceType === 0 && moment.utc(deadline).year() < 2000) return false;
    }
    if (field === 3 && isBeforeMinimum(executionDate)) return false;
    if (field === PARAMETER_FIELD && !selectedParameterDefinitionId) return false;

    if (!this.form || !this.form.current) return true;
    return this.form.current.reportValidity();
  }
  onSave = () => {
    const {
      selectedTaskStatus,
      field,
      deadline,
      executionDate,
      referenceType,
      referenceYear,
      selectedParameterDefinitionId,
      parameterValue,
    } = this.state;
    const { onSave } = this.props;
    if (!this.validate()) return Promise.reject(new Error('invalid form'));

    const parameters = {
      field,
    };
    if (field === 0) {
      parameters.deadline = deadline;
    } else if (field === 1) {
      // remap the extended task statuses (4 = inapplicable, 6 = rejected) to Done + a flag,
      // exactly like the single-task ChangeTaskStatusModal does.
      parameters.status =
        selectedTaskStatus === 4 || selectedTaskStatus === 6 ? 2 : selectedTaskStatus;
      parameters.inapplicable = selectedTaskStatus === 4;
      parameters.rejected = selectedTaskStatus === 6;
    } else if (field === 2) {
      switch (referenceType) {
        case 1:
          parameters.referenceYear = referenceYear;
          break;
        case 2:
          parameters.referenceYear = 0;
          break;
        case 3:
          parameters.referenceYear = -1;
          break;
        default:
          return Promise.reject();
      }
    } else if (field === 3) {
      parameters.executionDate = executionDate;
    } else if (field === PARAMETER_FIELD) {
      if (!selectedParameterDefinitionId) return Promise.reject();
      parameters.parameterDefinitionId = selectedParameterDefinitionId;
      parameters.parameterValueText = parameterValue.valueText;
      parameters.parameterValueNumeric = parameterValue.valueNumeric;
      parameters.parameterValueDate = parameterValue.valueDate;
      parameters.parameterValueBoolean = parameterValue.valueBoolean;
    } else {
      return Promise.reject();
    }

    return onSave(parameters);
  };

  onCombinedFieldChange = e => {
    const { parameterDefinitions } = this.props;
    const { value } = e.target;
    if (value.startsWith('p-')) {
      const parameterDefinitionId = value.substring(2);
      const definition = (parameterDefinitions || []).find(
        x => String(x.id) === parameterDefinitionId,
      );
      if (!definition) return;
      this.setState({
        field: PARAMETER_FIELD,
        selectedParameterDefinitionId: parameterDefinitionId,
        parameterValue: defaultParameterValue(),
      });
    } else if (value.startsWith('f-')) {
      this.setState({
        field: Number(value.substring(2)),
        selectedParameterDefinitionId: null,
      });
    }
  };

  onParameterValueChange = (value, parameter, type) => {
    this.setState(state => {
      const parameterValue = { ...state.parameterValue };
      switch (type) {
        case 1: // numeric
          parameterValue.valueNumeric = value === '' || value === null ? null : Number(value);
          break;
        case 2: // date
          parameterValue.valueDate = value;
          break;
        case 3: // boolean
          parameterValue.valueBoolean = value;
          break;
        default: // text, textarea, selection list, employee (0, 4, 5, 6)
          parameterValue.valueText = value;
          break;
      }
      return { parameterValue };
    });
  };
  deadlineChanged = input => {
    this.setState({
      deadline: input,
    });
  };
  executionDateChanged = input => {
    this.setState({
      executionDate: input,
    });
  };
  refTypeChanged = e => {
    this.setState({ referenceType: parseInt(e.target.value) });
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  render() {
    const { show, intl, parameterDefinitions } = this.props;
    const {
      selectedTaskStatus,
      taskstatuses,
      field,
      deadline,
      executionDate,
      dateFocused,
      executionDateFocused,
      referenceType,
      referenceYear,
      fieldOptions,
      selectedParameterDefinitionId,
      parameterValue,
    } = this.state;

    const eligibleParameterDefinitions = (parameterDefinitions || []).filter(
      isEligibleParameterDefinition,
    );
    const selectedParameterDefinition = eligibleParameterDefinitions.find(
      x => String(x.id) === String(selectedParameterDefinitionId),
    );
    const selectedValue =
      field === PARAMETER_FIELD && selectedParameterDefinitionId
        ? `p-${selectedParameterDefinitionId}`
        : `f-${field}`;

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.ChangeMainTaskField)}
        show={show}
        onClose={this.closeModal}
        onSave={this.onSave}
        disabled={fieldOptions.length === 0}
      >
        {show && (
          <form ref={this.form}>
            <div className="form-horizontal">
              <div className="form-group">
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.Field)}
                </label>
                <div className="col-md-6">
                  <select
                    className="form-control"
                    value={selectedValue}
                    onChange={this.onCombinedFieldChange}
                  >
                    {fieldOptions.map(option => (
                      <option key={`f-${option.id}`} value={`f-${option.id}`}>
                        {option.label}
                      </option>
                    ))}
                    {eligibleParameterDefinitions.map(definition => (
                      <option key={`p-${definition.id}`} value={`p-${definition.id}`}>
                        {definition.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {field === PARAMETER_FIELD && selectedParameterDefinition && (
                <TaskDetailsParameterValue
                  parameter={{
                    ...selectedParameterDefinition,
                    ...parameterValue,
                    readOnly: false,
                  }}
                  onChange={this.onParameterValueChange}
                  labelClassName="control-label col-sm-3"
                  inputClassName="col-md-6"
                />
              )}
              {field === 0 && (
                <div
                  className={classNames('form-group', {
                    'has-error': isBeforeMinimum(deadline),
                  })}
                >
                  <label className="control-label col-sm-3">
                    <FormattedMessage {...messages.Deadline} />
                  </label>
                  <div className="col-md-6">
                    <DatePicker
                      placeholder={intl.formatMessage(messages.Deadline)}
                      date={deadline}
                      onDateChange={this.deadlineChanged}
                      focused={dateFocused}
                      onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                      id="taskDeadline"
                      block
                      small
                      isOutsideRange={falseFunc}
                    />
                  </div>
                </div>
              )}
              {field === 3 && (
                <div
                  className={classNames('form-group', {
                    'has-error': isBeforeMinimum(executionDate),
                  })}
                >
                  <label className="control-label col-sm-3">
                    <FormattedMessage {...messages.ExecutionDate} />
                  </label>
                  <div className="col-md-6">
                    <DatePicker
                      placeholder={intl.formatMessage(messages.ExecutionDate)}
                      date={executionDate}
                      onDateChange={this.executionDateChanged}
                      focused={executionDateFocused}
                      onFocusChange={({ focused }) =>
                        this.setState({ executionDateFocused: focused })
                      }
                      id="taskExecutionDate"
                      block
                      small
                      isOutsideRange={falseFunc}
                    />
                  </div>
                </div>
              )}
              {field === 1 && (
                <div className="form-group">
                  <label className="control-label col-sm-3">
                    {intl.formatMessage(messages.TaskStatus)}
                  </label>
                  <div className="col-md-6">
                    <select
                      onChange={this.taskStatusChanged}
                      className="form-control"
                      value={selectedTaskStatus || ''}
                    >
                      {taskstatuses &&
                        taskstatuses.map(taskstatus => (
                          <option key={taskstatus.key} value={taskstatus.key}>
                            {taskstatus.label}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              )}
              {field === 2 && (
                <div className="form-group">
                  <label className="control-label col-sm-3">
                    {intl.formatMessage(messages.ReferenceYear)}
                  </label>
                  <div className="col-md-9">
                    <label>
                      <input
                        id="override"
                        type="radio"
                        name="refType"
                        value={1}
                        checked={referenceType === 1}
                        onChange={this.refTypeChanged}
                        defaultChecked
                      />
                      <label htmlFor="override" className="margin5L">
                        {intl.formatMessage(messages.OverrideReferenceYear)}
                      </label>
                      <YearSelect
                        id="referenceYearField"
                        value={referenceYear}
                        required
                        onChange={year => this.setState({ referenceYear: year })}
                        onFocus={() => this.setState({ referenceType: 1 })}
                        className="form-control"
                      />
                    </label>
                    <label>
                      <input
                        id="zero"
                        type="radio"
                        name="refType"
                        value={2}
                        checked={referenceType === 2}
                        onChange={this.refTypeChanged}
                      />
                      <label htmlFor="zero" className="margin5L">
                        {intl.formatMessage(messages.ClearReferenceYear)}
                      </label>
                    </label>
                    <label>
                      <input
                        id="recalc"
                        type="radio"
                        name="refType"
                        value={3}
                        checked={referenceType === 3}
                        onChange={this.refTypeChanged}
                      />
                      <label htmlFor="recalc" className="margin5L">
                        {intl.formatMessage(messages.RecalculateReferenceYear)}
                      </label>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}
